import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../api/api';
import { fmtId, fmtDatumNL } from '../util/format';
import { useLijst, KopRij, GefilterdChips, markeer } from '../components/lijst';

/* Toezicht & handhaving op het lijstontwerp van 26-08-2026. Twee werklijsten,
   elk met eigen sortering en filters. Geen grote zoekbalk en geen paneel:
   dit zijn kleine werkvoorraden, de loepjes in de kop en de klikbare chips
   (ontbrekende gegevens, afdichtingsverzoek-status) zijn hier genoeg. */

const afdichtLabel = (p) => {
  const st = p.afdicht_status?.status;
  if (st === 'herinnering_mogelijk') return 'Herinnering nodig';
  if (st === 'verstuurd_recent') return 'Verzonden';
  return 'Nog niet verstuurd';
};

const ontbrekendeVoor = (p) => {
  const lijst = [];
  if (p.gemeten_diepte_m == null) lijst.push('Gemeten diepte');
  if (p.diepte_bv_filter_m == null) lijst.push('Filterdiepte (bovenkant)');
  if (!p.aanleg_datum) lijst.push('Datum aanleg');
  return lijst;
};

export default function Veldwerk() {
  const [onvolledig, setOnvolledig] = useState([]);
  const [nietGekoppeld, setNietGekoppeld] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const ng = useLijst({
    facetten: [
      { key: 'afdicht', label: 'Afdichtingsverzoek', waarde: (p) => [afdichtLabel(p)] },
    ],
    kopzoek: {
      coord: { label: 'Coördinaten', tekst: (p) => `${Math.round(p.x_rd)}, ${Math.round(p.y_rd)}` },
      perceel: { label: 'Perceel', tekst: (p) => p.perceel || '' },
      houder: { label: 'Laatste houder', tekst: (p) => p.laatste_houder_naam || '' },
    },
    sorteer: {
      put_id: (p) => p.put_id,
      coordinaten: (p) => p.x_rd,
      perceel: (p) => p.perceel || '',
      laatste_houder: (p) => p.laatste_houder_naam || '',
      laatste_eind_datum: (p) => p.laatste_eind_datum || '',
      afdicht_status: afdichtLabel,
    },
  });

  const onv = useLijst({
    facetten: [
      { key: 'ontbrekend', label: 'Ontbrekende gegevens', waarde: ontbrekendeVoor },
    ],
    kopzoek: {
      coord: { label: 'Coördinaten', tekst: (p) => `${Math.round(p.x_rd)}, ${Math.round(p.y_rd)}` },
      perceel: { label: 'Perceel', tekst: (p) => p.perceel || '' },
    },
    sorteer: {
      put_id: (p) => p.put_id,
      coordinaten: (p) => p.x_rd,
      perceel: (p) => p.perceel || '',
      ontbrekend: (p) => ontbrekendeVoor(p).join(','),
    },
  });

  useEffect(() => { laad(); }, []);

  const laad = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get('/putten', { params: { onvolledig: true } }),
        api.get('/putten', { params: { niet_gekoppeld: true } }),
      ]);
      setOnvolledig(a.data);
      setNietGekoppeld(b.data);
    } finally {
      setLoading(false);
    }
  };

  const totaal = onvolledig.length + nietGekoppeld.length;

  const NG_KOLOMMEN = [
    { key: 'put_id', label: 'ID' },
    { key: 'coordinaten', label: 'Coördinaten (RD)', loep: 'coord' },
    { key: 'perceel', label: 'Perceel', loep: 'perceel' },
    { key: 'laatste_houder', label: 'Laatste houder', loep: 'houder' },
    { key: 'laatste_eind_datum', label: 'Tot' },
    { key: 'afdicht_status', label: 'Afdichtingsverzoek' },
  ];

  const ONV_KOLOMMEN = [
    { key: 'put_id', label: 'ID' },
    { key: 'coordinaten', label: 'Coördinaten (RD)', loep: 'coord' },
    { key: 'perceel', label: 'Perceel', loep: 'perceel' },
    { key: 'ontbrekend', label: 'Ontbrekende gegevens' },
  ];

  const ngGefilterd = ng.filterEnSorteer(nietGekoppeld);
  const onvGefilterd = onv.filterEnSorteer(onvolledig);

  return (
    <div className="main-inner">
      <div className="page-header">
        <div>
          <h1 className="page-title">Toezicht & handhaving</h1>
          <p className="page-subtitle">
            {totaal === 0
              ? 'Geen openstaande zaken'
              : `${totaal} ${totaal === 1 ? 'put vraagt' : 'putten vragen'} aandacht`}
          </p>
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><span className="spinner" /></div>
      ) : (
        <>
          <div className="card" style={{ padding: 0 }}>
            <div className="card-header" style={{ padding: '20px 24px', marginBottom: 0 }}>
              <h3 className="card-title">Niet gekoppelde putten</h3>
              <span className="chip">{nietGekoppeld.length}</span>
            </div>
            <div style={{ padding: '0 24px' }}><GefilterdChips lijst={ng} /></div>
            {nietGekoppeld.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>
                ✓ Alle actieve putten zijn gekoppeld aan een inrichting.
              </div>
            ) : (
              <table className="table pl-tabel">
                <thead><KopRij lijst={ng} kolommen={NG_KOLOMMEN} /></thead>
                <tbody>
                  {ngGefilterd.length === 0 ? (
                    <tr><td colSpan={NG_KOLOMMEN.length} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: 32 }}>
                      Geen rijen voldoen aan de filter.
                    </td></tr>
                  ) : ngGefilterd.map(p => {
                    const kz = ng.kopzoek;
                    const coordTekst = `${Math.round(p.x_rd)}, ${Math.round(p.y_rd)}`;
                    return (
                      <tr key={p.put_id} className="clickable"
                          onClick={() => navigate(`/beregening/putten/${p.put_id}`)}>
                        <td><span className="mono mono-id">{fmtId(p.put_id)}</span></td>
                        <td className="pl-cel-dim">
                          {kz.coord ? markeer(coordTekst, kz.coord) : coordTekst}
                        </td>
                        <td className="pl-cel-mono">
                          {p.perceel
                            ? (kz.perceel ? markeer(p.perceel, kz.perceel) : p.perceel)
                            : <span className="pl-leeg">–</span>}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {p.laatste_houder_id ? (
                            <Link to={`/beregening/houders/${p.laatste_houder_id}`}>
                              {kz.houder
                                ? markeer(p.laatste_houder_naam, kz.houder)
                                : p.laatste_houder_naam}
                            </Link>
                          ) : (
                            <span style={{ color: 'var(--text-muted)' }}>onbekend</span>
                          )}
                        </td>
                        <td>{p.laatste_eind_datum ? fmtDatumNL(p.laatste_eind_datum) : <span className="pl-leeg">–</span>}</td>
                        <td>
                          {(() => {
                            const st = p.afdicht_status?.status;
                            const klik = (e) => {
                              e.stopPropagation();
                              ng.zetFilter('afdicht', afdichtLabel(p));
                            };
                            if (st === 'herinnering_mogelijk') {
                              return <span className="pl-status" onClick={klik}
                                           title="Klik om hierop te filteren">
                                <span className="pl-dot" style={{ background: 'var(--pl-amber)' }} />
                                Herinnering nodig · verzonden {fmtDatumNL(p.afdicht_status.verzonden_op)}
                              </span>;
                            }
                            if (st === 'verstuurd_recent') {
                              return <span className="pl-status" onClick={klik}
                                           title="Klik om hierop te filteren">
                                <span className="pl-dot" style={{ background: 'var(--pl-groen)' }} />
                                Verzonden op {fmtDatumNL(p.afdicht_status.verzonden_op)}
                              </span>;
                            }
                            return <span className="pl-status" onClick={klik}
                                         title="Klik om hierop te filteren">
                              <span className="pl-dot" style={{ background: 'var(--pl-grijs)' }} />
                              Nog niet verstuurd
                            </span>;
                          })()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <div className="card" style={{ padding: 0, marginTop: 20 }}>
            <div className="card-header" style={{ padding: '20px 24px', marginBottom: 0 }}>
              <h3 className="card-title">Veldwerkgegevens onbekend</h3>
              <span className="chip">{onvolledig.length}</span>
            </div>
            <div style={{ padding: '0 24px' }}><GefilterdChips lijst={onv} /></div>
            {onvolledig.length === 0 ? (
              <div style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>
                ✓ Geen putten met ontbrekende veldwerkgegevens.
              </div>
            ) : (
              <table className="table pl-tabel">
                <thead><KopRij lijst={onv} kolommen={ONV_KOLOMMEN} /></thead>
                <tbody>
                  {onvGefilterd.length === 0 ? (
                    <tr><td colSpan={ONV_KOLOMMEN.length} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: 32 }}>
                      Geen rijen voldoen aan de filter.
                    </td></tr>
                  ) : onvGefilterd.map(p => {
                    const kz = onv.kopzoek;
                    const coordTekst = `${Math.round(p.x_rd)}, ${Math.round(p.y_rd)}`;
                    return (
                      <tr key={p.put_id} className="clickable"
                          onClick={() => navigate(`/beregening/putten/${p.put_id}`)}>
                        <td><span className="mono mono-id">{fmtId(p.put_id)}</span></td>
                        <td className="pl-cel-dim">
                          {kz.coord ? markeer(coordTekst, kz.coord) : coordTekst}
                        </td>
                        <td className="pl-cel-mono">
                          {p.perceel
                            ? (kz.perceel ? markeer(p.perceel, kz.perceel) : p.perceel)
                            : <span className="pl-leeg">–</span>}
                        </td>
                        <td>
                          <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 4 }}>
                            {ontbrekendeVoor(p).map(naam => (
                              <span
                                key={naam}
                                className="chip-clickable"
                                title="Klik om hierop te filteren"
                                onClick={(e) => { e.stopPropagation(); onv.zetFilter('ontbrekend', naam); }}
                                style={{
                                  display: 'inline-block',
                                  background: 'var(--danger-soft)',
                                  color: 'var(--danger)',
                                  padding: '2px 10px',
                                  borderRadius: 12,
                                  fontSize: 12,
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                }}>{naam}</span>
                            ))}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
